import CloudDoneIcon from '@mui/icons-material/CloudDone'; // 🟢 Synchronisiert
import CloudSyncIcon from '@mui/icons-material/CloudSync'; // 🟡 Bearbeitet

import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from "axios";
import { Button, MenuItem, Select, TextField, Typography, Box, Dialog, DialogActions, DialogContent } from "@mui/material";
import { useSessionStore, labelMap } from "./SessionStore"; // Zustand-Store für die aktuelle Sitzung
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SessionDetails from './SessionDetails';
import { useSiteSettings } from './context/SiteSettingsContext';
import { useUsageStatus } from "./hooks/useUsageStatus";
import { useDialog } from './components/Modal/DialogContext';
import { useUser } from './context/UserContext';
import { transcribeDictation } from "./utils/dictation";
import AudioRecorderPanel from "./components/AudioRecorderPanel";
import Tooltip from './components/Tooltip';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
type ConfigureDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

type RecordingState = "inactive" | "recording" | "paused";
type TemplateSource = "legacy" | "documentation_template" | "user_documentation_template";

type TemplateOption = {
  id: number;
  name: string;
  active: boolean;
  entity_type: string;
  for_intake?: boolean;
  for_progress?: boolean;
  for_biography?: boolean;
  for_summary?: boolean;
  for_individual?: boolean;
  for_couple?: boolean;
  for_group?: boolean;
  for_report?: boolean;
  for_dialogue?: boolean;
  for_vt?: boolean;
  for_st?: boolean;
  for_tp?: boolean;
  for_at?: boolean;
  for_kjp?: boolean;
  for_kjp_child?: boolean;
  for_kjp_parents?: boolean;
  for_kjp_family?: boolean;
  template_source: TemplateSource;
  selection_key: string;
  has_user_overrides?: boolean;
  user_override_count?: number;
  template_detail_url?: string;
};

type TemplateFavorite = {
  template_source: "documentation_template" | "user_documentation_template";
  template_id: number;
};

const normalizeDocumentationType = (value: string) => {
  const normalized = (value || "").toLowerCase();
  if (normalized === "verlaufsdokumentation") return "progress";
  if (normalized === "erstgespräch" || normalized === "erstgespraech") return "intake";
  if (normalized === "biografie") return "biography";
  if (normalized === "zusammenfassung") return "summary";
  return normalized || "unspecified";
};

const normalizeParticipant = (value: string) => {
  const normalized = (value || "").toLowerCase();
  if (normalized === "einzel") return "individual";
  if (normalized === "paar") return "couple";
  if (normalized === "gruppe") return "group";
  return normalized || "unspecified";
};

function buildTemplateFavoriteContextKey({
  isKjpActive,
  selectedParticipation,
  kjpContext,
  selectedDocumentationType,
  selectedTranscriptType,
  selectedTherapyForm,
}: {
  isKjpActive: boolean;
  selectedParticipation: string;
  kjpContext?: string | null;
  selectedDocumentationType: string;
  selectedTranscriptType?: string | null;
  selectedTherapyForm?: string | null;
}) {
  const target = isKjpActive ? "kjp" : "adult";
  const audiencePart = isKjpActive
    ? `kjp_context:${kjpContext || "unspecified"}`
    : `participant:${normalizeParticipant(selectedParticipation)}`;

  return [
    "v1",
    "entity:session",
    `target:${target}`,
    audiencePart,
    `doc:${normalizeDocumentationType(selectedDocumentationType)}`,
    `transcript:${selectedTranscriptType || "unspecified"}`,
    `therapy:${selectedTherapyForm || "unspecified"}`,
  ].join("|");
}

function favoriteToSelectionKey(favorite?: TemplateFavorite | null) {
  if (!favorite) return null;
  if (favorite.template_source === "documentation_template") {
    return `documentation:${favorite.template_id}`;
  }
  if (favorite.template_source === "user_documentation_template") {
    return `user:${favorite.template_id}`;
  }
  return null;
}

function selectionKeyToFavorite(selectionKey?: string | null): TemplateFavorite | null {
  if (!selectionKey) return null;
  const [source, rawId] = selectionKey.split(":");
  const templateId = Number(rawId);
  if (!Number.isFinite(templateId)) return null;
  if (source === "documentation") {
    return { template_source: "documentation_template", template_id: templateId };
  }
  if (source === "user") {
    return { template_source: "user_documentation_template", template_id: templateId };
  }
  return null;
}

const ConfigureDialogContent: React.FC<ConfigureDialogProps> = ({ open, setOpen }) => {
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const dialog = useDialog();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const supplementsEnabled = !!siteSettings?.supplements_enabled;
  const structuredTemplatesEnabled = !!siteSettings?.structured_templates_enabled;
  const customerTemplateGenerationMode = siteSettings?.customer_template_generation_mode ?? "legacy";
  const { user } = useUser();
  const isAdminOrInternal = !!(user?.is_staff || user?.is_admin_user || user?.is_internal);
  const canUseStructuredTemplates = !!(isAdminOrInternal || user?.has_template_dual_access || customerTemplateGenerationMode === "structured");
  const canUseLegacyTemplates = !!(isAdminOrInternal || user?.has_template_dual_access || customerTemplateGenerationMode !== "structured");
  const canUseBothTemplateSources = canUseStructuredTemplates && canUseLegacyTemplates;
  const sessionData = useSessionStore((state) => state.sessionData);
  const selectedSessionId = useSessionStore((state) => state.selectedSessionId);
  const fetchSessionDataById = useSessionStore((state) => state.fetchSessionDataById);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedNoteType, setSelectedNoteType] = useState("erstgespräch");
  const [selectedLocation, setSelectedLocation] = useState("Persönlich");
  const [selectedParticipation, setSelectedParticipation] = useState("einzel");
  const [selectedTranscriptType, setSelectedTranscriptType] = useState<"dialogue" | "report" | null>(() => {
    const transcriptType = sessionData?.transcript_type;
    return transcriptType === "dialogue" || transcriptType === "report" ? transcriptType : null;
  });
  const sessions = useSessionStore((s) => s.sessions);
  const currentSession = sessions.find((s) => s.id === selectedSessionId);
  const isCompleted = currentSession?.tempStatus === "completed";
  

  const [selectedGender, setSelectedGender] = useState<"male" | "female">("female");
  const [clientLabel, setClientLabel] = useState<"client" | "patient" | "customer">("client");
  
  type Gender = "male" | "female";
  type ClientLabel = "client" | "patient" | "customer";
  const clientLabelMap: Record<ClientLabel, Record<Gender, string>> = {
    client: { male: "Klient", female: "Klientin" },
    patient: { male: "Patient", female: "Patientin" },
    customer: { male: "Kunde", female: "Kundin" }
  };
  const [promptTemplates, setPromptTemplates] = useState<TemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [templateFavorites, setTemplateFavorites] = useState<Record<string, TemplateFavorite>>({});
  const [templateFavoritesLoaded, setTemplateFavoritesLoaded] = useState(false);
  const [savingTemplateFavorite, setSavingTemplateFavorite] = useState(false);
  const lastTemplateFavoriteContextKeyRef = useRef<string>("");
  const lastTemplateFavoritesLoadedRef = useRef(false);
  const templateSelectionTouchedRef = useRef(false);
  const [llmModels, setLlmModels] = useState<string[]>([
    "meta-llama/Llama-3.3-70B-Instruct",
    "meta-llama/Meta-Llama-3.1-70B-Instruct",
    "mistralai/Mistral-7B-Instruct-v0.3",
    "meta-llama/Meta-Llama-3.1-8B-Instruct",
    "mistralai/Mixtral-8x7B-Instruct-v0.1",
  ]);
  const [selectedLlm, setSelectedLlm] = useState<string>("meta-llama/Llama-3.3-70B-Instruct");
  const [sessionName, setSessionName] = useState(sessionData?.name || "");
  const [therapyForm, setTherapyForm] = useState<"vt" | "st" | "tp" | "at" | null>(null);
  const [isKjp, setIsKjp] = useState(false);
  const [kjpContext, setKjpContext] = useState<"child" | "parents" | "family" | null>(null);
  const [childAge, setChildAge] = useState("");
  const [supplementsText, setSupplementsText] = useState("");
  const supplementsTextRef = useRef("");
  const supplementsTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const supplementsRecordingRef = useRef(false);
  const supplementsRecorderRef = useRef<MediaRecorder | null>(null);
  const supplementsChunksRef = useRef<Blob[]>([]);
  const [supplementsRecording, setSupplementsRecording] = useState(false);
  const [supplementsHasCursor, setSupplementsHasCursor] = useState(false);
  const [supplementsProcessing, setSupplementsProcessing] = useState(false);
  const [supplementsOverlayVisible, setSupplementsOverlayVisible] = useState(false);
  const [supplementsOverlayExpanded, setSupplementsOverlayExpanded] = useState(false);
  const supplementsOverlayCloseTimerRef = useRef<number | null>(null);
  const [fachDictationOpen, setFachDictationOpen] = useState(false);
  const [fachDictationState, setFachDictationState] = useState<RecordingState>("inactive");
  const [fachDictationTime, setFachDictationTime] = useState(0);
  const [fachDictationUploading, setFachDictationUploading] = useState(false);
  const [fachDictationError, setFachDictationError] = useState<string | null>(null);
  const fachDictationRecorderRef = useRef<MediaRecorder | null>(null);
  const fachDictationStreamRef = useRef<MediaStream | null>(null);
  const fachDictationAudioContextRef = useRef<AudioContext | null>(null);
  const fachDictationAnalyserRef = useRef<AnalyserNode | null>(null);
  const fachDictationDataArrayRef = useRef<Uint8Array | null>(null);
  const fachDictationVolumeBarRef = useRef<HTMLCanvasElement | null>(null);
  const fachDictationChunksRef = useRef<Blob[]>([]);
  const fachDictationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fachDictationStateRef = useRef<RecordingState>("inactive");
  const fachDictationDiscardRef = useRef(false);
  const fachDictationMimeRef = useRef<string>("audio/webm;codecs=opus");
  // Formular-Schritt: 1 = Name, 2 = Restliches Formular
  const [formStep, setFormStep] = useState<1 | 2>(1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(sessionData?.date ? new Date(sessionData.date) : null);


  // Dynamische, editierbare Felder aus der Dokumentation

  const [currentInputIndex, setCurrentInputIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { data: usageStatus, isLoading: isUsageLoading } = useUsageStatus();
  const isKjpActive = isKjp;
  const isStep2Valid =
    sessionName.trim() !== "" &&
    !!selectedDate &&
    !!selectedTemplate &&
    !!selectedNoteType &&
    !!selectedParticipation &&
    !!selectedGender &&
    (sessionData?.recording_type === "upload" ? !!selectedTranscriptType : true) &&
    (isKjpActive ? childAge.trim() !== "" : true);

 // Wenn auf Dialog gewechselt wird, darf "Gruppe" nicht aktiv bleiben
  useEffect(() => {
    if (selectedTranscriptType === "dialogue" && selectedParticipation === "Gruppe") {
      setSelectedParticipation("Einzel");
    }
  }, [selectedTranscriptType, selectedParticipation]);
  
  useEffect(() => {
    axios.get("/api/apps/note-settings")
      .then((res) => {
        if (res.data.client_label) {
          setClientLabel(res.data.client_label);
        }
        if (res.data.therapy_form) {
          setTherapyForm(res.data.therapy_form);
        }
        if (res.data.is_kjp !== undefined) {
          setIsKjp(!!res.data.is_kjp);
        }
      })
      .catch((err) => {
        console.error("Fehler beim Laden des client_label:", err);
      });
  }, []);

  useEffect(() => {
    axios.get("/api/apps/template-favorites/")
      .then((res) => {
        setTemplateFavorites(res.data?.favorites || {});
      })
      .catch((err) => {
        console.warn("Template-Favorites konnten nicht geladen werden:", err);
        setTemplateFavorites({});
      })
      .finally(() => setTemplateFavoritesLoaded(true));
  }, []);

  
  useEffect(() => {
    // Immer den sessionName resetten, auch wenn leer
    setSessionName(sessionData?.name ?? "");
    setFormStep(sessionData?.name ? 2 : 1);

    if (sessionData?.gender) {
      setSelectedGender(sessionData.gender === "female" ? "female" : "female");
    }


    if (sessionData?.session_type) {
      if (sessionData.session_type === "progress_note") {
        setSelectedNoteType("verlaufsdokumentation");
      } else if (sessionData.session_type === "intake") {
        setSelectedNoteType("erstgespräch");
      } else if (sessionData.session_type === "biography") {
        setSelectedNoteType("biografie");
      } else if (sessionData.session_type === "summary") {
        setSelectedNoteType("zusammenfassung");
      }
    }

    if (sessionData?.participation_type) {
      const participationMap: Record<string, string> = {
        individual: "Einzel",
        couple: "Paar",
        group: "Gruppe",
      };
      setSelectedParticipation(participationMap[sessionData.participation_type] || "Einzel");
    }
    if (sessionData?.location) {
      const locationMap: Record<string, string> = {
        in_person: "Persönlich",
        online: "Online",
        clients_home: "Hausbesuch",
      };
      setSelectedLocation(locationMap[sessionData.location] || "Persönlich");
    }
    if (sessionData?.kjp_context) {
      setKjpContext(sessionData.kjp_context);
    } else {
      setKjpContext(null);
    }
    if (sessionData?.child_age !== undefined && sessionData?.child_age !== null) {
      setChildAge(String(sessionData.child_age));
    } else {
      setChildAge("");
    }

    if (sessionData?.date) {
      setSelectedDate(new Date(sessionData.date));
    } else {
      setSelectedDate(null);
    }
    const supplements = sessionData?.data?.supplements;
    if (Array.isArray(supplements) && supplements.length > 0) {
      let latestContent = "";
      for (let i = supplements.length - 1; i >= 0; i -= 1) {
        const entry = supplements[i];
        if (entry && typeof entry.content === "string" && entry.content.trim()) {
          latestContent = entry.content;
          break;
        }
      }
      setSupplementsText(latestContent);
    } else {
      setSupplementsText("");
    }
    const transcriptType = sessionData?.transcript_type;
    setSelectedTranscriptType(
      transcriptType === "dialogue" || transcriptType === "report" ? transcriptType : null
    );
  }, [sessionData, selectedSessionId]);

  useEffect(() => {
    if (open) {
      templateSelectionTouchedRef.current = false;
      lastTemplateFavoriteContextKeyRef.current = "";
      lastTemplateFavoritesLoadedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    supplementsTextRef.current = supplementsText;
  }, [supplementsText]);

  useEffect(() => {
    if (!open) {
      if (supplementsOverlayCloseTimerRef.current) {
        window.clearTimeout(supplementsOverlayCloseTimerRef.current);
        supplementsOverlayCloseTimerRef.current = null;
      }
      setSupplementsOverlayVisible(false);
      setSupplementsOverlayExpanded(false);
    }
  }, [open]);

  useEffect(() => {
    if (!supplementsOverlayVisible) return;
    const stopArrowKeys = (event: KeyboardEvent) => {
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
        event.stopPropagation();
      }
    };
    window.addEventListener("keydown", stopArrowKeys, true);
    return () => window.removeEventListener("keydown", stopArrowKeys, true);
  }, [supplementsOverlayVisible]);

  useEffect(() => {
    if (!fachDictationOpen) {
      if (fachDictationStateRef.current !== "inactive") {
        fachDictationDiscardRef.current = true;
        fachDictationRecorderRef.current?.stop();
        setFachDictationState("inactive");
        fachDictationStateRef.current = "inactive";
      }
      if (fachDictationTimerRef.current) {
        clearInterval(fachDictationTimerRef.current);
        fachDictationTimerRef.current = null;
      }
      if (fachDictationStreamRef.current) {
        fachDictationStreamRef.current.getTracks().forEach((track) => track.stop());
        fachDictationStreamRef.current = null;
      }
      if (fachDictationAudioContextRef.current) {
        fachDictationAudioContextRef.current.close().catch(() => {});
        fachDictationAudioContextRef.current = null;
      }
      setFachDictationTime(0);
    }
  }, [fachDictationOpen]);

  useEffect(() => {
    const filtered = getFilteredTemplates();
    const contextKey = buildTemplateFavoriteContextKey({
      isKjpActive,
      selectedParticipation,
      kjpContext,
      selectedDocumentationType: selectedNoteType,
      selectedTranscriptType,
      selectedTherapyForm: therapyForm,
    });
    const contextChanged = lastTemplateFavoriteContextKeyRef.current !== contextKey;
    const favoritesJustLoaded = templateFavoritesLoaded && !lastTemplateFavoritesLoadedRef.current;
    const favoriteSelectionKey = favoriteToSelectionKey(templateFavorites[contextKey]);
    const favoriteIsAvailable = !!favoriteSelectionKey && filtered.some((tpl) => tpl.selection_key === favoriteSelectionKey);

    if (contextChanged) {
      templateSelectionTouchedRef.current = false;
    }

    if (filtered.length > 0) {
      setSelectedTemplate((currentSelection) => {
        const currentSelectionIsAvailable = !!currentSelection && filtered.some((tpl) => tpl.selection_key === currentSelection);
        if (
          favoriteIsAvailable &&
          (contextChanged || favoritesJustLoaded || !currentSelectionIsAvailable)
        ) {
          return favoriteSelectionKey || filtered[0].selection_key;
        }
        if (currentSelectionIsAvailable) {
          return currentSelection;
        }
        return filtered[0].selection_key;
      });
    } else if (selectedTemplate !== "") {
      setSelectedTemplate("");
    }
    lastTemplateFavoriteContextKeyRef.current = contextKey;
    lastTemplateFavoritesLoadedRef.current = templateFavoritesLoaded;
  }, [open, selectedNoteType, selectedParticipation, selectedTranscriptType, promptTemplates, therapyForm, isKjpActive, kjpContext, selectedTemplate, templateFavorites, templateFavoritesLoaded]);


  useEffect(() => {
    const fetchPromptTemplates = async () => {
      try {
        const requests = [axios.get("/api/apps/fetch-templates/")];
        if (structuredTemplatesEnabled) {
          requests.push(axios.get("/api/apps/fetch-documentation-templates/"));
          if (canUseStructuredTemplates) {
            requests.push(
              axios.get("/api/apps/user-templates/").catch((error) => {
                console.warn("UserTemplates konnten nicht geladen werden:", error);
                return { data: [] } as any;
              })
            );
          }
        }
        const [legacyResponse, structuredResponse, userTemplatesResponse] = await Promise.all(requests);
        const legacyTemplates: TemplateOption[] = (legacyResponse.data || []).map((tpl: any) => ({
          ...tpl,
          template_source: "legacy",
          selection_key: `legacy:${tpl.id}`,
        }));
        const structuredTemplates: TemplateOption[] = structuredTemplatesEnabled
          ? ((structuredResponse?.data || []) as any[]).map((tpl) => ({
              ...tpl,
              template_source: "documentation_template",
              selection_key: `documentation:${tpl.id}`,
            }))
          : [];
        const userTemplates: TemplateOption[] = structuredTemplatesEnabled && canUseStructuredTemplates
          ? ((userTemplatesResponse?.data || []) as any[]).map((tpl) => {
              const hasAnyTherapyFlag = !!(tpl.for_vt || tpl.for_st || tpl.for_tp || tpl.for_at);
              return {
                ...tpl,
                name: tpl.name || tpl.title,
                template_source: "user_documentation_template",
                selection_key: `user:${tpl.id}`,
                for_vt: hasAnyTherapyFlag ? tpl.for_vt : true,
                for_st: hasAnyTherapyFlag ? tpl.for_st : true,
                for_tp: hasAnyTherapyFlag ? tpl.for_tp : true,
                for_at: hasAnyTherapyFlag ? tpl.for_at : true,
              };
            })
          : [];
        setPromptTemplates([
          ...(canUseLegacyTemplates ? legacyTemplates : []),
          ...structuredTemplates,
          ...userTemplates,
        ]);
      } catch (error) {
        console.error("Fehler beim Laden der PromptTemplates:", error);
      }
    };
  
    fetchPromptTemplates();
  }, [structuredTemplatesEnabled, canUseStructuredTemplates, canUseLegacyTemplates]);


  const getFilteredTemplates = () => {
    if (!Array.isArray(promptTemplates) || promptTemplates.length === 0) return [];

    const noteType = selectedNoteType?.toLowerCase() ?? "";
    const participation = selectedParticipation?.toLowerCase() ?? "";
    const transcriptType = selectedTranscriptType ?? "";

    return promptTemplates.filter((tpl) => {
      if (tpl.entity_type !== "session") return false;

      // Filter für inaktive Templates
      if (!tpl.active) return false;
      if (isKjpActive && !tpl.for_kjp) return false;
      if (!isKjpActive && tpl.for_kjp) return false;
      if (isKjpActive && kjpContext) {
        if (kjpContext === "child" && !tpl.for_kjp_child) return false;
        if (kjpContext === "parents" && !tpl.for_kjp_parents) return false;
        if (kjpContext === "family" && !tpl.for_kjp_family) return false;
      }
      if (noteType === "verlaufsdokumentation" && !tpl.for_progress) return false;
      if (noteType === "erstgespräch" && !tpl.for_intake) return false;
      if (noteType === "biografie" && !tpl.for_biography) return false;
      if (noteType === "zusammenfassung" && !tpl.for_summary) return false;

      if (!isKjpActive) {
        if (participation === "paar" && !tpl.for_couple) return false;
        if (participation === "gruppe" && !tpl.for_group) return false;
        if (participation === "einzel" && !tpl.for_individual) return false;
      }

      if (transcriptType === "dialogue" && !tpl.for_dialogue) return false;
      if (transcriptType === "report" && !tpl.for_report) return false;
      // Therapieform-Filter ergänzen
      if (therapyForm) {
        if (therapyForm === "vt" && !tpl.for_vt) return false;
        if (therapyForm === "st" && !tpl.for_st) return false;
        if (therapyForm === "tp" && !tpl.for_tp) return false;
        if (therapyForm === "at" && !tpl.for_at) return false;
      }
      return true;
    });
  };

  const selectedTemplateObject = promptTemplates.find((tpl) => tpl.selection_key === selectedTemplate) || null;
  const currentTemplateFavoriteContextKey = buildTemplateFavoriteContextKey({
    isKjpActive,
    selectedParticipation,
    kjpContext,
    selectedDocumentationType: selectedNoteType,
    selectedTranscriptType,
    selectedTherapyForm: therapyForm,
  });
  const currentTemplateFavoriteSelectionKey = favoriteToSelectionKey(templateFavorites[currentTemplateFavoriteContextKey]);
  const isSelectedTemplateFavorite = !!selectedTemplate && currentTemplateFavoriteSelectionKey === selectedTemplate;
  const selectedTemplateFavorite = selectionKeyToFavorite(selectedTemplate);
  const canFavoriteSelectedTemplate = !!selectedTemplateFavorite;

  const saveTemplateFavorite = async () => {
    if (!selectedTemplateFavorite) return;

    const previousTemplateFavorites = templateFavorites;
    setTemplateFavorites((currentFavorites) => ({
      ...currentFavorites,
      [currentTemplateFavoriteContextKey]: selectedTemplateFavorite,
    }));
    setSavingTemplateFavorite(true);
    try {
      const response = await axios.put("/api/apps/template-favorites/", {
        context_key: currentTemplateFavoriteContextKey,
        template_source: selectedTemplateFavorite.template_source,
        template_id: selectedTemplateFavorite.template_id,
      });
      setTemplateFavorites(response.data?.favorites || {});
    } catch (error) {
      setTemplateFavorites(previousTemplateFavorites);
      console.error("Template-Favorite konnte nicht gespeichert werden:", error);
    } finally {
      setSavingTemplateFavorite(false);
    }
  };

  const removeTemplateFavorite = async () => {
    const previousTemplateFavorites = templateFavorites;
    setTemplateFavorites((currentFavorites) => {
      const nextFavorites = { ...currentFavorites };
      delete nextFavorites[currentTemplateFavoriteContextKey];
      return nextFavorites;
    });
    setSavingTemplateFavorite(true);
    try {
      const response = await axios.delete("/api/apps/template-favorites/", {
        data: { context_key: currentTemplateFavoriteContextKey },
      });
      setTemplateFavorites(response.data?.favorites || {});
    } catch (error) {
      setTemplateFavorites(previousTemplateFavorites);
      console.error("Template-Favorite konnte nicht entfernt werden:", error);
    } finally {
      setSavingTemplateFavorite(false);
    }
  };

  const toggleTemplateFavorite = () => {
    if (isSelectedTemplateFavorite) {
      removeTemplateFavorite();
      return;
    }
    saveTemplateFavorite();
  };

  const templateFavoriteTooltip = isSelectedTemplateFavorite
    ? "Standard für diese Auswahl entfernen"
    : "Als Standard für diese Auswahl speichern";

  // Funktion zum Senden der Dokumentationsanforderung
  const handleGenerateDocumentation = async () => {
    if (!selectedTemplate) {
      alert("Bitte ein Prompt-Template auswählen!");
      return;
    }
    if (isKjpActive && childAge.trim() === "") {
      alert("Bitte das Alter des Kindes angeben.");
      return;
    }
    if (!sessionData || !sessionData.id) {
      alert("Keine Sitzung ausgewählt!");
      return;
    }
    if (!selectedTemplateObject) {
      alert("Das ausgewählte Template konnte nicht gefunden werden.");
      return;
    }

    setLoading(true);

    try {
      const kjpPayload: Record<string, unknown> = {};
      if (isKjpActive) {
        const trimmedAge = childAge.trim();
        const parsedAge = trimmedAge === "" ? null : Number(trimmedAge);
        const ageValue = parsedAge !== null && Number.isFinite(parsedAge) ? parsedAge : null;
        kjpPayload.kjp_context = kjpContext;
        kjpPayload.child_age = ageValue;
      }
      // Update der Session vor der Generierung
      await axios.put("/api/apps/update-session/", {
        id: sessionData.id,
        gender: selectedGender === "female" ? "female" : "male",
        session_type:
          selectedNoteType === "verlaufsdokumentation"
            ? "progress_note"
            : selectedNoteType === "biografie"
              ? "biography"
              : selectedNoteType === "zusammenfassung"
                ? "summary"
                : "intake",
        location:
          selectedLocation === "Persönlich"
            ? "in_person"
            : selectedLocation === "Online"
              ? "online"
              : "clients_home",
        name: sessionName,
        participation_type:
          selectedParticipation === "Einzel"
            ? "individual"
            : selectedParticipation === "Paar"
              ? "couple"
              : "group",
        date:
          selectedDate &&
            sessionData?.date &&
            selectedDate.toDateString() !== new Date(sessionData.date).toDateString()
            ? new Date(
              selectedDate.getFullYear(),
              selectedDate.getMonth(),
              selectedDate.getDate(),
              new Date(sessionData.date).getHours(),
              new Date(sessionData.date).getMinutes(),
              new Date(sessionData.date).getSeconds()
            ).toISOString()
            : undefined,
        template_used: selectedTemplateObject.name || "",
        transcript_type: selectedTranscriptType,
        ...kjpPayload,
      });

      if (selectedTemplateObject.template_source === "documentation_template") {
        const payload: Record<string, unknown> = {
          user_sitzung_id: sessionData.id,
          documentation_template_id: selectedTemplateObject.id,
        };
        if (supplementsEnabled) {
          payload.supplements_text = supplementsText;
        }
        await axios.post("/api/services/generate-documentation-template/", payload);
      } else if (selectedTemplateObject.template_source === "user_documentation_template") {
        const payload: Record<string, unknown> = {
          user_sitzung_id: sessionData.id,
          template_source: "user_template",
          user_documentation_template_id: selectedTemplateObject.id,
        };
        if (supplementsEnabled) {
          payload.supplements_text = supplementsText;
        }
        await axios.post("/api/services/generate-documentation-template/", payload);
      } else {
        const payload: Record<string, unknown> = {
          user_sitzung_id: sessionData.id,
          prompt_template_id: selectedTemplateObject.id,
          llm_model: selectedLlm,
        };
        if (supplementsEnabled) {
          payload.supplements_text = supplementsText;
        }
        await axios.post("/api/services/generate-documentation/", payload);
      }
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["usage-status"] });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        dialog({
          type: "alert",
          title: "Hinweis",
          message: error.response?.data?.error || "Maximal 4 Dokumentationen erlaubt. Bitte zuerst eine löschen.",
        });
        return;
      }
      console.error("Fehler beim Generieren der Dokumentation:", error);
      alert("Fehler beim Generieren der Dokumentation.");
    } finally {
      setLoading(false);
    }
  };

  const insertSupplementsWithTypewriter = (text: string) => {
    const textarea = supplementsTextareaRef.current;
    const currentText = supplementsTextRef.current;
    const selectionStart = textarea?.selectionStart ?? currentText.length;
    const selectionEnd = textarea?.selectionEnd ?? selectionStart;
    const before = currentText.slice(0, selectionStart);
    const after = currentText.slice(selectionEnd);
    const prevChar = before.slice(-1);
    const nextChar = after.slice(0, 1);
    const needsSpaceBefore = prevChar && !/\s/.test(prevChar);
    const needsSpaceAfter = nextChar && !/\s/.test(nextChar);
    const finalText = `${needsSpaceBefore ? " " : ""}${text}${needsSpaceAfter ? " " : ""}`;
    let currentIndex = 0;
    const baseIndex = before.length;
    const typeNextChar = () => {
      if (currentIndex < finalText.length) {
        const partial =
          before + finalText.slice(0, currentIndex + 1) + after;
        setSupplementsText(partial);
        currentIndex += 1;
        setTimeout(typeNextChar, 13);
      } else {
        const full = before + finalText + after;
        setSupplementsText(full);
        requestAnimationFrame(() => {
          if (textarea) {
            const newPos = baseIndex + finalText.length;
            textarea.focus();
            textarea.selectionStart = newPos;
            textarea.selectionEnd = newPos;
          }
        });
      }
    };
    typeNextChar();
  };

  const toggleSupplementsDictation = async () => {
    if (supplementsRecordingRef.current) {
      supplementsRecorderRef.current?.stop();
      supplementsRecordingRef.current = false;
      setSupplementsRecording(false);
      setSupplementsProcessing(true);
      return;
    }
    const textarea = supplementsTextareaRef.current;
    const hasCursorNow = textarea ? textarea.selectionStart !== null : false;
    if (!supplementsHasCursor && !hasCursorNow) return;
    setSupplementsProcessing(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      supplementsRecorderRef.current = recorder;
      supplementsChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          supplementsChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(supplementsChunksRef.current, { type: "audio/webm" });
        try {
          setSupplementsProcessing(true);
          const transcript = await transcribeDictation(audioBlob);
          if (transcript) {
            insertSupplementsWithTypewriter(transcript);
          }
        } catch (error) {
          console.error("Fehler beim Transkribieren:", error);
        } finally {
          setSupplementsProcessing(false);
        }
      };

      recorder.start();
      supplementsRecordingRef.current = true;
      setSupplementsRecording(true);
    } catch (error) {
      console.error("Fehler beim Zugriff auf das Mikrofon:", error);
    }
  };

  const openSupplementsOverlay = () => {
    if (supplementsOverlayCloseTimerRef.current) {
      window.clearTimeout(supplementsOverlayCloseTimerRef.current);
      supplementsOverlayCloseTimerRef.current = null;
    }
    setSupplementsOverlayVisible(true);
    requestAnimationFrame(() => {
      setSupplementsOverlayExpanded(true);
      requestAnimationFrame(() => {
        supplementsTextareaRef.current?.focus();
        setSupplementsHasCursor(true);
      });
    });
  };

  const closeSupplementsOverlay = () => {
    setSupplementsOverlayExpanded(false);
    if (supplementsOverlayCloseTimerRef.current) {
      window.clearTimeout(supplementsOverlayCloseTimerRef.current);
    }
    supplementsOverlayCloseTimerRef.current = window.setTimeout(() => {
      setSupplementsOverlayVisible(false);
      supplementsOverlayCloseTimerRef.current = null;
    }, 200);
  };

  const cleanupFachDictationStream = () => {
    if (fachDictationStreamRef.current) {
      fachDictationStreamRef.current.getTracks().forEach((track) => track.stop());
      fachDictationStreamRef.current = null;
    }
    if (fachDictationAudioContextRef.current) {
      fachDictationAudioContextRef.current.close().catch(() => {});
      fachDictationAudioContextRef.current = null;
    }
  };

  const stopFachDictationTimer = () => {
    if (fachDictationTimerRef.current) {
      clearInterval(fachDictationTimerRef.current);
      fachDictationTimerRef.current = null;
    }
  };

  const startFachDictationTimer = () => {
    stopFachDictationTimer();
    fachDictationTimerRef.current = setInterval(() => {
      setFachDictationTime((prev) => prev + 1);
    }, 1000);
  };

  const uploadFachDictation = async (audioBlob: Blob, mimeType: string) => {
    if (!sessionData?.id) {
      setFachDictationError("Keine Sitzung ausgewählt.");
      return;
    }
    setFachDictationUploading(true);
    setFachDictationError(null);
    try {
      const extension = mimeType.includes("wav") ? "wav" : "webm";
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `fachdiktat_${sessionData.id}_${timestamp}.${extension}`;
      const formData = new FormData();
      formData.append("audio", audioBlob, filename);
      formData.append("session_id", String(sessionData.id));
      await axios.post("/api/services/supplement-dictation/", formData);
      await fetchSessionDataById(sessionData.id);
      setFachDictationOpen(false);
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Senden des Fachdiktats:", error);
      setFachDictationError("Fachdiktat konnte nicht gespeichert werden.");
    } finally {
      setFachDictationUploading(false);
    }
  };

  const startFachDictation = async () => {
    if (!sessionData?.id) {
      setFachDictationError("Bitte zuerst eine Sitzung auswählen.");
      return;
    }
    setFachDictationError(null);
    setFachDictationTime(0);
    startFachDictationTimer();
    fachDictationDiscardRef.current = false;
    fachDictationChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      fachDictationStreamRef.current = stream;

      let mimeType = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "audio/wav";
        }
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        setFachDictationError("Dieser Browser unterstützt kein geeignetes Audioformat.");
        cleanupFachDictationStream();
        stopFachDictationTimer();
        return;
      }
      fachDictationMimeRef.current = mimeType;

      const audioContext = new AudioContext();
      fachDictationAudioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      fachDictationAnalyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      fachDictationDataArrayRef.current = dataArray;

      const recorder = new MediaRecorder(stream, { mimeType });
      fachDictationRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          fachDictationChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const shouldDiscard = fachDictationDiscardRef.current;
        fachDictationDiscardRef.current = false;
        const chunks = fachDictationChunksRef.current;
        fachDictationChunksRef.current = [];
        cleanupFachDictationStream();
        if (shouldDiscard || chunks.length === 0) {
          return;
        }
        const blob = new Blob(chunks, { type: fachDictationMimeRef.current });
        await uploadFachDictation(blob, fachDictationMimeRef.current);
      };

      recorder.start();
      fachDictationStateRef.current = "recording";
      setFachDictationState("recording");
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Starten des Fachdiktats:", error);
      setFachDictationError("Fachdiktat konnte nicht gestartet werden.");
      cleanupFachDictationStream();
      stopFachDictationTimer();
      setFachDictationState("inactive");
      fachDictationStateRef.current = "inactive";
    }
  };

  const pauseFachDictation = () => {
    if (fachDictationRecorderRef.current?.state === "recording") {
      fachDictationRecorderRef.current.pause();
      setFachDictationState("paused");
      fachDictationStateRef.current = "paused";
      stopFachDictationTimer();
    }
  };

  const resumeFachDictation = () => {
    if (fachDictationRecorderRef.current?.state === "paused") {
      fachDictationRecorderRef.current.resume();
      setFachDictationState("recording");
      fachDictationStateRef.current = "recording";
      startFachDictationTimer();
    }
  };

  const stopFachDictation = () => {
    if (!fachDictationRecorderRef.current) return;
    setFachDictationState("inactive");
    fachDictationStateRef.current = "inactive";
    fachDictationRecorderRef.current.stop();
    stopFachDictationTimer();
    setFachDictationTime(0);
  };

  const abortFachDictation = () => {
    fachDictationDiscardRef.current = true;
    const recorder = fachDictationRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.stop();
    }
    cleanupFachDictationStream();
    stopFachDictationTimer();
    setFachDictationTime(0);
    setFachDictationState("inactive");
    fachDictationStateRef.current = "inactive";
  };

  const closeFachDictationModal = () => {
    if (fachDictationStateRef.current !== "inactive") {
      abortFachDictation();
    }
    setFachDictationOpen(false);
  };

  return (
    <div  className="upload-form__container__main">
      <style>{`
        @keyframes supplementsPulse {
          0% { transform: scale(1); opacity: 0.9; }
          50% { transform: scale(1.18); opacity: 1; }
          100% { transform: scale(1); opacity: 0.9; }
        }
        .supplements-pulse {
          display: inline-block;
          animation: supplementsPulse 1s infinite ease-in-out;
        }
      `}</style>
      <fieldset disabled={!isCompleted || loading} style={{ border: "none", padding: 0, margin: 0 }}>
        <div className="upload-form__container__main__widget">
        {/* Schritt 1: Nur Name/Titel */}
        {formStep === 1 && (
          <>
            <div id="note-configure-name-1" className="upload-form__container__main__widget__session__timer">
              <div className="_Section_17y3r_1">
                <label className="_Label_17y3r_18 !tw-font-normal !tw-text-[12px] tw-text-[#040404]">Name/ Titel</label>
                <div className="_ClockContainer_17y3r_24">
                  <div className="_CustomInput_17y3r_32 _CustomInputWithClassName_17y3r_131">
                    <input
                      type="text"
                      className="_Value_17y3r_56"
                      style={{
                        width: "100%",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        font: "inherit",
                        padding: 0,
                      }}
                      value={sessionName}
                      onChange={(e) => setSessionName(e.target.value)}
                      placeholder="Titel eingeben"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="upload-form__container__main__widget__button">
              <button
                type="button"
                className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
                disabled={!sessionName.trim()}
                onClick={async () => {
                  if (!sessionData?.id) return;
                  setLoading(true);
                  try {
                    await axios.put("/api/apps/update-session/", {
                      id: sessionData.id,
                      name: sessionName,
                    });
                    setFormStep(2);
                  } catch (e) {
                    console.error("Fehler beim Speichern des Namens", e);
                    alert("Fehler beim Speichern.");
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                <div className="_btnFlex_19rto_32">{loading ? "Speichern..." : "Speichern und weiter"}</div>
              </button>
            </div>
          </>
        )}
        {/* Schritt 2: Restliches Formular */}
        {formStep === 2 && (
          <>
            <div id="note-configure-name-2" className="upload-form__container__main__widget__session__timer">
              <div className="_Section_17y3r_1">
                <label className="_Label_17y3r_18 !tw-font-normal !tw-text-[12px] tw-text-[#040404]">Name/ Titel</label>
                <div className="_ClockContainer_17y3r_24">
                  <div className="_CustomInput_17y3r_32 _CustomInputWithClassName_17y3r_131">
                    <input
                      type="text"
                      className="_Value_17y3r_56"
                      style={{
                        width: "100%",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        font: "inherit",
                        padding: 0,
                      }}
                      value={sessionName}
                      onChange={(e) => setSessionName(e.target.value)}
                      placeholder="Titel eingeben"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div id="note-configure-date" className="upload-form__container__main__widget__session__date">
              <label className="label !tw-font-normal">Datum</label>
              <div className="session-date">
                <div className="blocker" style={{ pointerEvents: "auto", width: "100%" }}>
                  <div className="DatePicker date-wrapper tw-relative" role="presentation">
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={selectedDate}
                        onChange={(newValue: Date | null) => {
                          setSelectedDate(newValue);
                          setPickerOpen(false);
                        }}
                        open={pickerOpen}
                        onClose={() => setPickerOpen(false)}
                        disableOpenPicker
                        PopperProps={{
                          sx: { zIndex: 2000 },
                          anchorEl: document.querySelector('.date-input'),
                        }}
                        renderInput={() => <></>}
                      />
                    </LocalizationProvider>
                    <input
                      readOnly
                      className="date-input !tw-cursor-pointer !tw-text-[#040404]"
                      style={{ paddingRight: 38 }}
                      value={selectedDate ? selectedDate.toLocaleDateString() : ""}
                      onClick={() => setPickerOpen(true)}
                    />
                    <button
                      type="button"
                      aria-label="Kalender öffnen"
                      onClick={() => setPickerOpen(true)}
                      className="btn-reset tw-absolute tw-top-1/2 left-1 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-text-gray-500"
                      style={{ transform: "translateY(-50%)", zIndex: 2 }}
                    >
                      <svg
                        aria-hidden="true"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect x="4" y="5" width="16" height="15" rx="3" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M8 3.5V7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        <path d="M16 3.5V7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        <path d="M4.5 9.5H19.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        <path d="M8 13H8.01M12 13H12.01M16 13H16.01M8 16.5H8.01M12 16.5H12.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {sessionData?.recording_type === "upload" && (
              <div id="note-configure-recording-kind" className="upload-form__container__main__widget__session__location">
                <label className="label !tw-font-normal">Art der Aufnahme</label>
                <div className="radio__group__each tw-flex tw-gap-x-4 sm:tw-gap-x-0 sm:tw-gap-y-0 tw-gap-y-2 tw-flex-wrap !tw-bg-white !tw-py-0 tw-items-center">
                  {[
                    {
                      value: "dialogue",
                      label: `Gespräch mit ${
                        {
                          client: "Klient:in",
                          patient: "Patient:in",
                          customer: "Kund:in",
                        }[clientLabel]
                      }`,
                    },
                    { value: "report", label: "Eingesprochenes Diktat" },
                  ].map((option) => {
                    const isSelected = selectedTranscriptType === option.value;
                    return (
                      <div key={option.value} className="sm:tw-w-1/3">
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <input
                            type="radio"
                            id={option.value}
                            name="recordingKind"
                            className="lumanoteEX__radio tw-cursor-pointer"
                            value={option.value}
                            checked={isSelected}
                            onChange={async () => {
                              if (!sessionData?.id) return;
                              setSelectedTranscriptType(option.value as "dialogue" | "report");
                            }}
                          />
                          <label
                            htmlFor={option.value}
                            className={`radio__group__each__label ${isSelected ? "!tw-text-black" : "!tw-text-gray"}`}
                          >
                            {option.label}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!selectedTranscriptType && (
                  <p className="text-xs mt-2 mb-0" style={{ color: "#e7a555" }}>
                    Bitte geben Sie an, ob es sich bei der Aufnahme um ein therapeutisches Gespräch oder um ein fachliches Diktat handelt.
                  </p>
                )}
              </div>
            )}
            {/*  <div className="upload-form__container__main__widget__session__timer">
                        <div className="_Section_17y3r_1">
                          <label className="_Label_17y3r_18 !tw-font-normal !tw-text-[12px] tw-text-[#040404]">Dauer der Sitzung</label>
                          <div className="_ClockContainer_17y3r_24">
                            <div className="_CustomInput_17y3r_32 _CustomInputWithClassName_17y3r_131">
                              <div className="tw-space-x-2"><span className="_Value_17y3r_56">{sessionData?.length || "N/A"}</span></div>
                            </div>
                          </div>
                        </div>
                      </div>*/}
            {isKjpActive ? (
              <>
                <div id="note-configure-kjp-context" className="upload-form__container__main__widget__session__location">
                  <label className="label !tw-font-normal">KJP-Kontext</label>
                  <div className="radio__group__each tw-flex tw-gap-x-4 sm:tw-gap-x-0 sm:tw-gap-y-0 tw-gap-y-2 tw-flex-wrap !tw-bg-white !tw-py-0 tw-items-center">
                    {[
                      { value: "child", label: "Kind/ Jugendlicher" },
                      { value: "parents", label: "Eltern/ Bezugsperson" },
                      { value: "family", label: "Familie/ Kombination" },
                    ].map((option) => {
                      const isSelected = kjpContext === option.value;
                      return (
                        <div key={option.value} className="sm:tw-w-1/3">
                          <div className="tw-flex tw-items-center tw-gap-2">
                            <input
                              type="radio"
                              id={`kjp-${option.value}`}
                              name="kjp-context"
                              className="lumanoteEX__radio tw-cursor-pointer"
                              value={option.value}
                              checked={isSelected}
                              onChange={() => setKjpContext(option.value as "child" | "parents" | "family")}
                            />
                            <label
                              htmlFor={`kjp-${option.value}`}
                              className={`radio__group__each__label ${
                                isSelected ? "!tw-text-black" : "!tw-text-gray"
                              }`}
                            >
                              {option.label}
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="tw-flex tw-flex-col gap-2 sm:tw-flex-row sm:gap-24">
                  <div
                    id="note-configure-kjp-age"
                    className="upload-form__container__main__widget__session__timer sm:tw-flex-none w-[100px] sm:flex-[0_0_100px]"
                    
                  >
                    <div className="_Section_17y3r_1">
                      <label className="_Label_17y3r_18 !tw-font-normal !text-[12px] text-[#040404]">Alter des Kindes</label>
                      <div className="_ClockContainer_17y3r_24">
                        <div className="_CustomInput_17y3r_32 _CustomInputWithClassName_17y3r_131">
                          <input
                            type="number"
                            min={0}
                            className="_Value_17y3r_56"
                            style={{
                              width: "100%",
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              font: "inherit",
                              padding: 0,
                            }}
                            value={childAge}
                            onChange={(e) => setChildAge(e.target.value)}
                            placeholder="z. B. 8"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    id="note-configure-gender"
                    className="upload-form__container__main__widget__gender sm:flex-1 sm:ml-1"
                    style={{ minWidth: "260px" }}
                  >
                    <label className="label !tw-font-normal">Anredeform im Text</label>
                    <div className="radio__group__each tw-flex tw-gap-x-4 sm:tw-gap-x-0 sm:tw-gap-y-0 tw-gap-y-2 tw-flex-wrap !tw-bg-white !tw-py-0 tw-items-center">
                      {["female", "male"].map((gender) => {
                        const label = isKjpActive
                          ? (gender === "female" ? "Sie" : "Er")
                          : (clientLabelMap[clientLabel]?.[gender as "female" | "male"] || gender);
                        const isSelected = selectedGender === gender;
                        return (
                          <div key={gender} className="sm:tw-w-1/3">
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <input
                                type="radio"
                                id={gender}
                                name="gender"
                                className="lumanoteEX__radio tw-cursor-pointer"
                                value={gender}
                                checked={isSelected}
                                onChange={() => setSelectedGender(gender as "male" | "female")}
                              />
                              <label htmlFor={gender} className={`radio__group__each__label ${isSelected ? "!tw-text-black" : "!tw-text-gray"}`}>
                                {label}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div id="note-configure-participant" className="upload-form__container__main__widget__session__location">
                <label className="label !tw-font-normal">Teilnehmer</label>
                <div className="radio__group__each tw-flex tw-gap-x-4 sm:tw-gap-x-0 sm:tw-gap-y-0 tw-gap-y-2 tw-flex-wrap !tw-bg-white !tw-py-0 tw-items-center">
                  {["Einzel", "Paar", "Gruppe"].map((option) => {
                    const isSelected = selectedParticipation === option;
                    const isGroup = option === "Gruppe";
                    const isDialogue = selectedTranscriptType === "dialogue";
                    const isDisabled = isGroup && isDialogue;
                    return (
                      <div key={option} className="sm:tw-w-1/3">
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <input
                            type="radio"
                            id={option}
                            name="location"
                            className={`lumanoteEX__radio ${isDisabled ? "tw-cursor-not-allowed" : "tw-cursor-pointer"}`}
                            value={option}
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => {
                              if (isDisabled) return;
                              setSelectedParticipation(option);
                            }}
                          />
                          <label
                            htmlFor={option}
                            className={`radio__group__each__label ${option === "Paar" ? "tooltip-container" : ""} ${
                              isSelected ? "!tw-text-black" : "!tw-text-gray"
                            } ${isDisabled ? "tw-opacity-50" : ""}`}
                          >
                            {option === "Paar" ? (
                              <>
                                Paar/ MPS
                                <span className="tooltip-bubble">
                                  Paare oder Mehrpersonensetting - z.B. mit Familienmitgliedern, Bezugspersonen, Betreuern etc.
                                </span>
                              </>
                            ) : option}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {!isKjpActive && (
              <div id="note-configure-gender" className="upload-form__container__main__widget__gender">
                <label className="label !tw-font-normal">Anrede</label>
                <div className="radio__group__each tw-flex tw-gap-x-4 sm:tw-gap-x-0 sm:tw-gap-y-0 tw-gap-y-2 tw-flex-wrap !tw-bg-white !tw-py-0 tw-items-center">
                  {["female", "male"].map((gender) => {
                    const label = clientLabelMap[clientLabel]?.[gender as "female" | "male"] || gender;
                    const isSelected = selectedGender === gender;
                    return (
                      <div key={gender} className="sm:tw-w-1/3">
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <input
                            type="radio"
                            id={gender}
                            name="gender"
                            className="lumanoteEX__radio tw-cursor-pointer"
                            value={gender}
                            checked={isSelected}
                            onChange={() => setSelectedGender(gender as "male" | "female")}
                          />
                          <label htmlFor={gender} className={`radio__group__each__label ${isSelected ? "!tw-text-black" : "!tw-text-gray"}`}>
                            {label}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* <div className="upload-form__container__main__widget__session !tw-flex">
                      <div className="upload-form__container__main__widget__name">
                        <label className="label tw-flex tw-gap-[0.1rem] !tw-font-normal !tw-mb-2">Client <span className="!tw-text-danger-500 tw-m-0">*</span></label>
                        <div className="css-b62m3t-container">
                          <div className="css-1m90hyn-control">
                            <div className="css-hlgwow">
                              <div className="css-1dimb5e-singleValue">
                                <div className="tw-flex tw-items-center tw-gap-2 tw-justify-between ">
                                  <div className="tw-text-sm tw-capitalize"><span className="tw-m-0 tw-flex">Lucy (example client)</span></div>
                                  <div className="tw-flex tw-items-center tw-align-center tw-gap-2">
                                    <div className="_badge_1ngyc_1 _badge-individual_1ngyc_54 tw-p-2">individual</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="add-new-client tw-relative">
                          <img className="client_img" src="path/to/add-icon.svg" alt="add new client" />
                          <p className="client_text">Add new client</p>
                        </div>
                      </div>
                    </div>*/}
            <div id="note-configure-type" className="upload-form__container__main__widget__note">
              <label className="label !tw-font-normal">Dokumentations-Typ</label>
              <div className="tw-flex tw-gap-x-4 sm:tw-gap-x-0 sm:tw-gap-3.5 tw-gap-y-2 tw-flex-wrap tw-items-center">
                {["erstgespräch", "verlaufsdokumentation", "biografie", "zusammenfassung"].map((type) => {
                  const isSelected = selectedNoteType === type;
                  return (
                    <div key={type} className="tw-flex sm:tw-w-1/3 tw-gap-x-1">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <input
                          type="radio"
                          id={type}
                          name="noteType"
                          value={type}
                          className="lumanoteEX__radio tw-cursor-pointer"
                          checked={isSelected}
                          onChange={() => setSelectedNoteType(type)}
                        />
                        <label
                          htmlFor={type}
                          className={`radio__group__each__label ${isSelected ? "!tw-text-black" : "!tw-text-gray"}`}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {supplementsEnabled && (
              <div id="note-configure-supplements" className="upload-form__container__main__widget__session__timer mt-3">
                <label className="label  !font-normal">Zusätzliche fachliche Hinweise (optional)</label>
                <div className="_ClockContainer_17y3r_24">
                  <div className="_CustomInput_17y3r_32 _CustomInputWithClassName_17y3r_131 tw-relative">
                    {supplementsRecording && (
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 9998 }}
                      />
                    )}
                    <textarea
                      ref={supplementsOverlayVisible ? undefined : supplementsTextareaRef}
                      className="_Value_17y3r_56 placeholder:text-xs"
                      style={{
                        width: "100%",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        font: "inherit",
                        padding: 0,
                        minHeight: "48px",
                        resize: "vertical",
                      }}
                      value={supplementsText}
                      onChange={(e) => setSupplementsText(e.target.value)}
                      onFocus={() => setSupplementsHasCursor(true)}
                      onBlur={() => {
                        if (!supplementsRecordingRef.current) {
                          setSupplementsHasCursor(false);
                        }
                      }}
                      onClick={() => setSupplementsHasCursor(true)}
                      onKeyUp={() => setSupplementsHasCursor(true)}
                      onSelect={() => setSupplementsHasCursor(true)}
                      disabled={supplementsOverlayVisible}
                      placeholder="Wichtige Beobachtungen, diagnostische Überlegungen, Hypothesen, Kontext zur Sitzung …"
                    />
                    {(supplementsRecording || supplementsProcessing) && !supplementsOverlayVisible && (
                      <div
                        className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-px-3 tw-rounded"
                        style={{
                          backgroundColor: supplementsRecording
                            ? "rgba(120, 130, 145, 0.65)"
                            : "rgba(67, 155, 232, 0.65)",
                          color: "#1f2937",
                          boxShadow: "0 6px 12px rgba(0,0,0,0.12)",
                          backdropFilter: "blur(14px)",
                          WebkitBackdropFilter: "blur(14px)",
                          border: "1px solid rgba(255,255,255,0.4)",
                          zIndex: 9999,
                        }}
                      >
                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                          <svg
                            viewBox="0 0 22 22"
                            width="36"
                            height="36"
                            aria-hidden="true"
                            fill="currentColor"
                            className={`${supplementsRecording ? "supplements-pulse " : ""}tw-inline-block`}
                          >
                            <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zm-5 9v-2h2v-2h-4v2h2v2h2z" />
                          </svg>
                          {supplementsRecording && (
                            <button
                              type="button"
                              className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
                              onClick={toggleSupplementsDictation}
                              style={{
                                backgroundColor: "rgba(255,255,255,0.9)",
                                color: "#1f2937",
                                border: "none",
                                borderRadius: "0.5rem",
                                padding: "0.25rem 0.75rem",
                                cursor: "pointer",
                                fontWeight: "bold",
                              }}
                            >
                              <div className="_btnFlex_19rto_32">Stopp</div>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-3">
                  <button
                    type="button"
                    className="btn-reset"
                    onClick={toggleSupplementsDictation}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      supplementsTextareaRef.current?.focus();
                      setSupplementsHasCursor(true);
                    }}
                    disabled={!supplementsHasCursor && !supplementsRecording}
                    aria-disabled={!supplementsHasCursor && !supplementsRecording}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      background: "transparent",
                      color: "#4caf50",
                      cursor: supplementsHasCursor ? "pointer" : "not-allowed",
                      opacity: supplementsHasCursor ? 1 : 0.3,
                      filter: supplementsHasCursor ? "none" : "grayscale(100%)",
                    }}
                  >
                    <svg viewBox="0 0 22 22" width="24" height="24" aria-hidden="true" fill="currentColor">
                      <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zm-5 9v-2h2v-2h-4v2h2v2h2z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="btn-reset"
                    onClick={openSupplementsOverlay}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      background: "transparent",
                      color: "#6b7280",
                      cursor: "pointer",
                    }}
                    aria-label="Supplements vergrößern"
                    title="Vergrößern"
                  >
                    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="currentColor">
                      <path d="M7 3H3v4h2V5h2V3zm12 0h-4v2h2v2h2V3zm0 18h-4v-2h2v-2h2v4zM7 21H3v-4h2v2h2v2z" />
                    </svg>
                  </button>
                </div>
                {supplementsOverlayVisible && (
                  <div
                    className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center"
                    style={{
                      zIndex: 10000,
                      backgroundColor: "rgba(15, 23, 42, 0.25)",
                      backdropFilter: "blur(6px)",
                    }}
                    onClick={() => {
                      if (!supplementsRecording && !supplementsProcessing) {
                        closeSupplementsOverlay();
                      }
                    }}
                  >
                    <div
                      className="tw-relative tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-2xl"
                      style={{
                        width: "92vw",
                        maxWidth: "980px",
                        transform: supplementsOverlayExpanded
                          ? "scale(1) translateY(0)"
                          : "scale(0.96) translateY(12px)",
                        opacity: supplementsOverlayExpanded ? 1 : 0,
                        transition: "transform 200ms ease, opacity 200ms ease",
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                        <div className="tw-text-sm tw-font-medium tw-text-slate-700">
                          Zusätzliche fachliche Hinweise (optional)
                        </div>
                        <button
                          type="button"
                          className="btn-reset"
                          onClick={closeSupplementsOverlay}
                          disabled={supplementsRecording || supplementsProcessing}
                          aria-label="Supplements verkleinern"
                          title="Verkleinern"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            background: "transparent",
                            color: "#6b7280",
                            cursor: supplementsRecording || supplementsProcessing ? "not-allowed" : "pointer",
                            opacity: supplementsRecording || supplementsProcessing ? 0.5 : 1,
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="currentColor">
                            <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                          </svg>
                        </button>
                      </div>
                      <div className="tw-relative tw-flex tw-items-start tw-gap-4">
                        <div className="tw-relative tw-flex-1">
                          <textarea
                            ref={supplementsOverlayVisible ? supplementsTextareaRef : undefined}
                            autoFocus={supplementsOverlayVisible}
                            className="_Value_17y3r_56"
                            style={{
                              width: "100%",
                              border: "1px solid rgba(15, 23, 42, 0.08)",
                              outline: "none",
                              background: "rgba(248, 250, 252, 0.9)",
                              font: "inherit",
                              padding: "16px",
                              minHeight: "320px",
                              resize: "vertical",
                              borderRadius: "12px",
                            }}
                            value={supplementsText}
                            onChange={(e) => setSupplementsText(e.target.value)}
                            onFocus={() => setSupplementsHasCursor(true)}
                            onBlur={() => {
                              if (!supplementsRecordingRef.current) {
                                setSupplementsHasCursor(false);
                              }
                            }}
                            onClick={() => setSupplementsHasCursor(true)}
                            onKeyUp={() => setSupplementsHasCursor(true)}
                            onSelect={() => setSupplementsHasCursor(true)}
                            placeholder="Wichtige Beobachtungen, diagnostische Überlegungen, Hypothesen, Kontext zur Sitzung …"
                          />
                          {(supplementsRecording || supplementsProcessing) && (
                            <div
                              className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-px-4 tw-rounded-xl"
                              style={{
                                backgroundColor: supplementsRecording
                                  ? "rgba(120, 130, 145, 0.65)"
                                  : "rgba(170, 205, 235, 0.65)",
                                color: "#1f2937",
                                boxShadow: "0 6px 12px rgba(0,0,0,0.12)",
                                backdropFilter: "blur(14px)",
                                WebkitBackdropFilter: "blur(14px)",
                                border: "1px solid rgba(255,255,255,0.4)",
                                zIndex: 9999,
                              }}
                            >
                              <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                                <svg
                                  viewBox="0 0 22 22"
                                  width="40"
                                  height="40"
                                  aria-hidden="true"
                                  fill="currentColor"
                                  className={`${supplementsRecording ? "supplements-pulse " : ""}tw-inline-block`}
                                >
                                  <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zm-5 9v-2h2v-2h-4v2h2v2h2z" />
                                </svg>
                                {supplementsRecording && (
                                  <button
                                    type="button"
                                    className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
                                    onClick={toggleSupplementsDictation}
                                    style={{
                                      backgroundColor: "rgba(255,255,255,0.9)",
                                      color: "#1f2937",
                                      border: "none",
                                      borderRadius: "0.5rem",
                                      padding: "0.25rem 0.75rem",
                                      cursor: "pointer",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    <div className="_btnFlex_19rto_32">Stopp</div>
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn-reset"
                          onClick={toggleSupplementsDictation}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            supplementsTextareaRef.current?.focus();
                            setSupplementsHasCursor(true);
                          }}
                          disabled={!supplementsHasCursor && !supplementsRecording}
                          aria-disabled={!supplementsHasCursor && !supplementsRecording}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            background: "transparent",
                            color: "#4caf50",
                            cursor: supplementsHasCursor ? "pointer" : "not-allowed",
                            opacity: supplementsHasCursor ? 1 : 0.3,
                            filter: supplementsHasCursor ? "none" : "grayscale(100%)",
                          }}
                        >
                          <svg viewBox="0 0 22 22" width="28" height="28" aria-hidden="true" fill="currentColor">
                            <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zm-5 9v-2h2v-2h-4v2h2v2h2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {false && (
                <>
                <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-2">
                  <label className="label !font-normal">Fachdiktat</label>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <button
                      type="button"
                      className="_button_19rto_40 _primary_19rto_127 _outlined_19rto_69 _block_19rto_52 !tw-font-normal"
                      onClick={() => setFachDictationOpen(true)}
                      disabled={!sessionData?.id || fachDictationUploading}
                    >
                      <div className="_btnFlex_19rto_32">Fachdiktat aufnehmen</div>
                    </button>
                    {fachDictationUploading && (
                      <span className="tw-text-sm tw-text-gray-500">Transkription läuft…</span>
                    )}
                  </div>
                  {fachDictationError && (
                    <p className="tw-text-[red] tw-m-0">{fachDictationError}</p>
                  )}
                </div>
                {fachDictationOpen && (
                  <div
                    className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center"
                    style={{
                      zIndex: 10000,
                      backgroundColor: "rgba(15, 23, 42, 0.25)",
                      backdropFilter: "blur(6px)",
                    }}
                    onClick={() => {
                      if (fachDictationStateRef.current === "inactive" && !fachDictationUploading) {
                        closeFachDictationModal();
                      }
                    }}
                  >
                    <div
                      className="tw-relative tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-2xl"
                      style={{ width: "92vw", maxWidth: "840px" }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                        <div className="tw-text-sm tw-font-medium tw-text-slate-700">Fachdiktat</div>
                        <button
                          type="button"
                          className="btn-reset"
                          onClick={closeFachDictationModal}
                          disabled={fachDictationUploading}
                          aria-label="Fachdiktat schließen"
                          title="Schließen"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            background: "transparent",
                            color: "#6b7280",
                            cursor: fachDictationUploading ? "not-allowed" : "pointer",
                            opacity: fachDictationUploading ? 0.5 : 1,
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="currentColor">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>
                      <AudioRecorderPanel
                        headerText="Fachdiktat"
                        recordingState={fachDictationState}
                        recordingTime={fachDictationTime}
                        onStart={startFachDictation}
                        onPause={pauseFachDictation}
                        onResume={resumeFachDictation}
                        onStop={stopFachDictation}
                        onAbort={abortFachDictation}
                        startLabel="Aufnahme starten"
                        stopLabel="Aufnahme beenden"
                        deleteLabel="Löschen"
                        durationLabel="Aufnahmedauer: flexibel"
                        startIcon={
                          <svg
                            width="33"
                            height="32"
                            viewBox="0 0 33 32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="tw-w-5"
                          >
                            <path
                              d="M16.513 20.6641C19.4597 20.6641 21.8464 18.2774 21.8464 15.3307V7.9974C21.8464 5.05073 19.4597 2.66406 16.513 2.66406C13.5664 2.66406 11.1797 5.05073 11.1797 7.9974V15.3307C11.1797 18.2774 13.5664 20.6641 16.513 20.6641Z"
                              stroke="currentColor"
                              strokeWidth="1.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                            <path
                              d="M6.32031 12.8672V15.1339C6.32031 20.7605 10.8936 25.3339 16.5203 25.3339C22.147 25.3339 26.7203 20.7605 26.7203 15.1339V12.8672"
                              stroke="currentColor"
                              strokeWidth="1.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                            <path
                              d="M14.6641 8.57219C15.8641 8.13219 17.1707 8.13219 18.3707 8.57219"
                              stroke="currentColor"
                              strokeWidth="1.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                            <path
                              d="M15.4531 11.3978C16.1598 11.2111 16.8931 11.2111 17.5998 11.3978"
                              stroke="currentColor"
                              strokeWidth="1.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                            <path
                              d="M16.5156 25.3359V29.3359"
                              stroke="currentColor"
                              strokeWidth="1.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                          </svg>
                        }
                        startButtonClassName="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal"
                        stopButtonClassName="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 _block_19rto_52 recorder__submit-btn !tw-font-normal"
                        volumeBarRef={fachDictationVolumeBarRef}
                        recordingStateRef={fachDictationStateRef}
                        analyserRef={fachDictationAnalyserRef}
                        dataArrayRef={fachDictationDataArrayRef}
                        overlayClassName="hidden"
                        showHeaderOverlay={false}
                      />
                    </div>
                  </div>
                )}
                </>
                )}
              </div>
            )}





            <div id="note-configure-templates"  className="selector__container">
              <div className="selector__container__main">
                <label className="label !tw-font-normal">
                  Template{" "}
                  <Tooltip text="Die verfügbaren Templates hängen von den oben gewählten Einstellungen ab, z.B. von Dokumentations-Typ, Teilnehmern und Therapieform.">
                    <span className="tw-cursor-help">ⓘ</span>
                  </Tooltip>
                </label>
                <div className="selector__container__create">
                  <div className="note__types">
	                    <div className="tw-relative tw-flex tw-items-center tw-justify-between tw-w-full">
	                      <div className="tw-flex-1">
<select
  className="note__types__span note-selected tw-w-full"
  style={{ paddingRight: 54 }}
  value={selectedTemplate ?? ""}
  onChange={(e) => {
    templateSelectionTouchedRef.current = true;
    setSelectedTemplate(e.target.value);
  }}
>
  {getFilteredTemplates().length === 0 ? (
    <option className="normal-case" value="">Kein passendes Template gefunden</option>

  ) : (
                              getFilteredTemplates().map((tpl, idx) => (
                                <option key={tpl.selection_key || idx} value={tpl.selection_key}>
                                  {tpl.name || `Template ${idx + 1}`}{tpl.template_source === "documentation_template" && tpl.has_user_overrides ? "\u00A0\u00A0•\u00A0\u00A0angepasst" : ""}{canUseBothTemplateSources && tpl.template_source === "documentation_template" ? " [Structured]" : tpl.template_source === "user_documentation_template" ? "\u00A0\u00A0•\u00A0\u00A0eigene Vorlage" : ""}
                                </option>
                              ))
  )}
</select>
	                      </div>
		                      <div
		                        className="tw-absolute tw-top-1/2"
		                        style={{ right: 24, transform: "translateY(-50%)", zIndex: 2 }}
		                      >
			                        <Tooltip text={templateFavoriteTooltip}>
				                        <button
				                          type="button"
				                          aria-label={templateFavoriteTooltip}
				                          onClick={toggleTemplateFavorite}
		                            disabled={savingTemplateFavorite}
		                            className="btn-reset tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center"
		                            style={{
		                              color: isSelectedTemplateFavorite ? "#E7A555" : "#6b7280",
		                              opacity: canFavoriteSelectedTemplate && !savingTemplateFavorite ? 1 : 0.45,
		                              cursor: canFavoriteSelectedTemplate && !savingTemplateFavorite ? "pointer" : "not-allowed",
		                            }}
		                          >
	                            {isSelectedTemplateFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
	                          </button>
	                        </Tooltip>
	                      </div>
                    </div>  
                  </div>
                </div>
                {selectedTemplateObject?.template_source === "documentation_template" && selectedTemplateObject.has_user_overrides ? (
                  <div className="mt-2 tw-text-[12px] tw-text-gray-600">
                    Diese Vorlage enthält persönliche Anpassungen.{" "}
                    <Link className="tw-font-medium tw-text-gray-700 tw-underline" to={selectedTemplateObject.template_detail_url || `/my-templates/${selectedTemplateObject.id}`}>
                      Vorlage öffnen
                    </Link>
                  </div>
                ) : null}
              </div>
            </div>
            
            <div className="upload-form__container__main__widget__button">
              <button
                type="button"
                className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
                onClick={handleGenerateDocumentation}
                disabled={
                  !isCompleted ||
                  loading ||
                  !isStep2Valid ||
                  getFilteredTemplates().length === 0 ||
                  (!isUsageLoading && usageStatus && !usageStatus.has_notes_left)
                }
              >
                <div className="_btnFlex_19rto_32">
                  {!isCompleted || loading
                    ? "Generiere..."
                    : !isUsageLoading && usageStatus && !usageStatus.has_notes_left
                    ? "Limit erreicht"
                    : "Generiere Dokumentation"}
                </div>
              </button>

              {!isUsageLoading && usageStatus && !usageStatus.has_notes_left && (
                <p className="tw-text-sm tw-text-gray-600 tw-mt-2 tw-text-center">
                  Sie haben Ihr Dokumentationslimit erreicht.{" "}
                  <Link
                    to="/settings/planpicker"
                    className="tw-text-primary-600 tw-underline hover:tw-text-primary-800"
                  >
                    Jetzt Plan buchen oder upgraden
                  </Link>
                  , um mehr Dokumentationen zu erstellen.
                </p>
              )}
            </div>
          </>
        )}
        </div>
      </fieldset>
    </div>
  )
}
export default ConfigureDialogContent;

 
